"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import type { VenueConfig } from "@aluume/shared/lib/types/venue";
import { stagingGuestPrefill } from "@aluume/shared/lib/staging-prefill";
export type { VenueConfig };
import { Calendar } from "./Calendar";
import { writePendingAddons, type AddonLine } from "./AddOnsTile";
import { PackagesInfoTile, writePendingPackages, startPendingGeneration, type PackageLine } from "./PackagesTile";
import { nzd, nzTime, todayNZ, addDays } from "@aluume/shared/lib/format";
import { adultsMaxFor, adultsMinFor, childrenMaxFor } from "@aluume/shared/lib/party-limits";
import { fmtNZDate, isYmd } from "@aluume/shared/lib/time";
import { KIDS_FREE_LABEL } from "@aluume/shared/lib/kids-free";
import { captureAttribution, trackClientEvent } from "@aluume/shared/lib/tracking/client";
import { writePendingHold, readPendingHold } from "@aluume/shared/lib/booking/pending-hold";
import { CountrySelect } from "@/components/checkout/CountrySelect";
import { COUNTRIES } from "@aluume/shared/lib/countries";
import { combinePhone } from "@aluume/shared/lib/phone";
import { Spinner } from "@aluume/shared/components/ui/Spinner";
import { AGENT_REFERENCE_MAX } from "@aluume/shared/lib/agents/reference";
import { clearPromoLinkSafe, promoFromSearch, readPromoLinkSafe } from "@aluume/shared/lib/promo-link";

const NZ_COUNTRY = COUNTRIES.find((c) => c.iso === "NZ")!;
const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Slot = {
  resource_id: string;
  resource_slug: string;
  resource_name: string;
  booking_model: "exclusive" | "capacity";
  start_at: string;
  seats_left: number | null;
  tier_id: string | null;
  tier_name: string;
  price: string;
  // Remarkables only: the guest SOAK length (75 min) as returned by
  // get_remarkables_availability, threaded straight into the hold — create_hold
  // validates p_duration against the template's session_minutes and rejects
  // anything else (invalid_duration). The engine adds the 15-min clean slot itself
  // (clean_until), reserving the full 90-min block; the app never sends 90.
  duration_minutes?: number;
};
// Up to three experiences. "exclusive" = The Remarkables (Jeremy 2026-10-29): its own
// toggle option with one space card, the way Communal shows The Commons — it only
// exists where config.remarkables does (Bathe). Underneath it books exactly as
// before: kind=remarkables availability + booking_kind='remarkables' (see isRem).
type Mode = "private" | "communal" | "exclusive";


const TIER_COLOR: Record<string, string> = {
  "First Light": "#C8A96A", Reset: "#62684B", Apres: "#A69580", Twilight: "#7E4B27", "Late Night": "#656B73",
};
const TIER_ORDER = ["First Light", "Reset", "Apres", "Twilight", "Late Night"];

function makeKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "k-" + Date.now() + "-" + Math.random().toString(36).slice(2, 12);
}
// "2 adults + 2 children" / "2 adults" / "1 adult + 1 child" — for the
// too-big-for-this-pool error copy.
function partyPhrase(adults: number, children: number): string {
  const a = `${adults} ${adults === 1 ? "adult" : "adults"}`;
  if (children <= 0) return a;
  return `${a} + ${children} ${children === 1 ? "child" : "children"}`;
}
// fmtNZDate anchors the YMD at noon NZ (the old `T12:00:00Z` anchor was midnight
// NZ on the NEXT day, so chips/summary displayed one day ahead).
const fmtWeekday = (ymd: string) => fmtNZDate(ymd, { weekday: "short" });
const fmtDay = (ymd: string) => fmtNZDate(ymd, { day: "numeric" });
const fmtDateLong = (ymd: string) => fmtNZDate(ymd, { weekday: "short", day: "numeric", month: "short", year: "numeric" });


/**
 * A gentler scroll than the browser's `behavior: "smooth"` (Jeremy, 2026-09-30: "a bit sharp").
 * Ease-in-out over 700–1100ms depending on distance. Any wheel / touch / key from the guest
 * cancels it, so the page never fights someone who has started scrolling themselves.
 */
let glideFrame = 0;
function glideTo(target: number) {
  cancelAnimationFrame(glideFrame);
  const start = window.scrollY;
  const delta = target - start;
  if (Math.abs(delta) < 2) return;
  const duration = Math.min(1100, 700 + Math.abs(delta) * 0.4);
  const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const t0 = performance.now();
  const stop = () => {
    cancelAnimationFrame(glideFrame);
    window.removeEventListener("wheel", stop);
    window.removeEventListener("touchstart", stop);
    window.removeEventListener("keydown", stop);
  };
  window.addEventListener("wheel", stop, { passive: true });
  window.addEventListener("touchstart", stop, { passive: true });
  window.addEventListener("keydown", stop);
  const step = (now: number) => {
    const t = Math.min(1, (now - t0) / duration);
    window.scrollTo(0, start + delta * ease(t));
    if (t < 1) glideFrame = requestAnimationFrame(step);
    else stop();
  };
  glideFrame = requestAnimationFrame(step);
}

export function BookingFlow({ config, initialMode = "private", initialDate, initialAdults = 2, agent = false }: { config: VenueConfig; initialMode?: Mode; initialDate?: string; initialAdults?: number; agent?: boolean }) {
  const router = useRouter();
  const durationOptions = config.allowedDurations?.length ? config.allowedDurations : [60];
  // A ?experience=exclusive link on a venue with no Remarkables falls back to Private.
  const [mode, setMode] = useState<Mode>(initialMode === "exclusive" && !config.remarkables ? "private" : initialMode);
  // The party-limit helpers and the base-price API only know the two pool models; Exclusive
  // never reaches them (every call site branches on isRem first), so it maps to "private".
  const engineMode: "private" | "communal" = mode === "communal" ? "communal" : "private";
  // Pre-opening (Thermae): the earliest bookable day is the opening date, not
  // today — the strip, calendar and initial selection all start there so the
  // guest lands on day one instead of two months of empty days. Single source:
  // config.openingDate (the Members-page date). Bathe: openingDate is null.
  const openFloor =
    config.openingDate && config.openingDate > todayNZ() ? config.openingDate : todayNZ();
  // A malformed ?date= (bad link) falls back to the floor — unguarded it reached
  // addDays and threw "Invalid time value", 500ing the whole page (Sentry K).
  const clampOpen = (d: string) => (!isYmd(d) || d < openFloor ? openFloor : d);
  const [date, setDate] = useState(clampOpen(initialDate ?? todayNZ()));
  // The venue's first (default) session length — there is no picker (see "Choose a time").
  const [duration] = useState<number>(durationOptions[0]);
  // An Exclusive deep link starts at the hire's 2-guest floor at least.
  const [adults, setAdults] = useState(mode === "exclusive" && config.remarkables ? Math.max(initialAdults, config.remarkables.minParty) : initialAdults);
  const [children, setChildren] = useState(0);
  const [poolSlug, setPoolSlug] = useState(config.privatePools?.[0]?.slug ?? "");
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [subtotal, setSubtotal] = useState(0);
  // Adult/child split of the subtotal (from /api/price) so the summary can show
  // a broken-out "N × Child" row. childSubtotal is 0 for communal/no-children.
  const [adultSubtotal, setAdultSubtotal] = useState(0);
  const [childSubtotal, setChildSubtotal] = useState(0);
  // Server-asserted: a kids-free window is what zeroed the child line (not "it's $0").
  const [kidsFree, setKidsFree] = useState(false);
  // Upsells chosen on this page (the Upsells tile in the packages row) — surfaced by name in the summary,
  // folded into the fee base + total, and carried to checkout via sessionStorage.
  const [addonLines, setAddonLines] = useState<AddonLine[]>([]);
  // Packages (0366) — Soak + Sauna cross-promo / class packs, offered once a time is picked.
  const [packageLines, setPackageLines] = useState<PackageLine[]>([]);
  // ONE selection for both responsive copies of the tile; mirrored to sessionStorage so checkout
  // adopts it once the hold exists.
  const [packageSel, setPackageSel] = useState<Record<string, number>>({});
  // Guided scroll (Jeremy, 2026-09-29/30): each choice gently CENTRES the next step on screen —
  // Bathe experience → the toggle at the top (alignTop), pool → the date + "who's coming" together, date → the times,
  // time → the packages. Guest-count changes never move the page. Only user clicks trigger it, and
  // it waits a frame so newly rendered content is measured. A step taller than the screen is
  // aligned to its top instead, so its beginning is never scrolled out of view.
  const poolRef = useRef<HTMLElement>(null);
  const dateRef = useRef<HTMLElement>(null);
  const guestsRef = useRef<HTMLElement>(null);
  const timeRef = useRef<HTMLElement>(null);
  const packagesRef = useRef<HTMLElement>(null);
  const experienceRef = useRef<HTMLElement>(null);
  // Bathe's experience tabs all land in ONE spot (Jeremy 2026-10-29): the toggle row sits 24px
  // from the top, with the pool or space card right under it — whichever tab was tapped.
  const alignTop = useCallback((ref: React.RefObject<HTMLElement | null>) => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const target = Math.max(0, window.scrollY + r.top - 24);
      if (reduce) { window.scrollTo({ top: target }); return; }
      glideTo(target);
    }));
  }, []);
  const centreOn = useCallback((...refs: React.RefObject<HTMLElement | null>[]) => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const rects = refs.map((r) => r.current?.getBoundingClientRect()).filter((r): r is DOMRect => !!r && r.height > 0);
      if (!rects.length) return;
      const top = Math.min(...rects.map((r) => r.top));
      const bottom = Math.max(...rects.map((r) => r.bottom));
      const vh = window.innerHeight;
      const target = bottom - top > vh - 48
        ? window.scrollY + top - 24
        : window.scrollY + (top + bottom) / 2 - vh / 2;
      if (reduce) { window.scrollTo({ top: Math.max(0, target) }); return; }
      glideTo(Math.max(0, target));
    }));
  }, []);
  // Upsell products (the venue's POS upsell list) — the same add-on stash checkout adopts.
  const [upsellSel, setUpsellSel] = useState<Record<string, number>>({});
  // Every load of the booking page starts EMPTY (Jeremy, 2026-09-30: a refresh must not keep old
  // picks). The stash only carries picks made on THIS page view across to checkout, so a stale
  // one from an earlier visit is cleared here rather than adopted later.
  useEffect(() => { startPendingGeneration(); writePendingPackages({}); writePendingAddons({}); }, []);
  // A package voucher's "Book now" link (/bathe?voucher=CODE): shown against the total in the
  // summary here, and carried to checkout, which applies it for real (server-validated again there).
  const [linkVoucher, setLinkVoucher] = useState<{ code: string; balance: number } | null>(null);
  const [linkVoucherError, setLinkVoucherError] = useState("");
  useEffect(() => {
    let code: string | null = null;
    try {
      const v = new URLSearchParams(window.location.search).get("voucher");
      if (v && /^[A-Za-z0-9-]{4,64}$/.test(v)) {
        code = v.trim();
        sessionStorage.setItem("aluume_pending_voucher", code);
      }
    } catch { /* private mode */ }
    if (!code) return;
    let active = true;
    fetch("/api/voucher-validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, total: 100000, venue: config.venue }),
    })
      .then(async (r) => ({ ok: r.ok, j: await r.json().catch(() => ({})) }))
      .then(({ ok, j }) => {
        if (!active) return;
        if (ok) setLinkVoucher({ code: code as string, balance: Number(j.balance ?? 0) });
        else {
          setLinkVoucherError(`Voucher ${code}: ${j.error ?? "can't be used here"}`);
          try { sessionStorage.removeItem("aluume_pending_voucher"); } catch { /* nothing */ }
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [config.venue]);
  // A promo link (?promo=CODE on ANY page — lib/promo-link.ts): shown against the session here so
  // the page total matches checkout, which applies it for real (server-validated again there).
  // The URL is read directly as well as storage: this effect runs before the root capture's.
  const [linkPromo, setLinkPromo] = useState<{ code: string; kind: "percent" | "fixed"; value: number } | null>(null);
  const [linkPromoError, setLinkPromoError] = useState("");
  useEffect(() => {
    if (agent) return;
    let code: string | null = null;
    try { code = promoFromSearch(window.location.search) ?? readPromoLinkSafe(); } catch { /* nothing */ }
    if (!code) return;
    let active = true;
    fetch("/api/promo-validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, subtotal: 0 }),
    })
      .then(async (r) => ({ status: r.status, j: await r.json().catch(() => ({})) }))
      .then(({ status, j }) => {
        if (!active) return;
        if (status === 200 && (j.kind === "percent" || j.kind === "fixed")) {
          setLinkPromo({ code: code as string, kind: j.kind, value: Number(j.value ?? 0) });
        } else if (status === 404 || status === 422) {
          // Conclusively unusable (unknown, expired, not yet active, used up) — say so once, drop it.
          setLinkPromoError(`Code ${code}: ${j.error ?? "can't be used"}`);
          clearPromoLinkSafe();
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [agent]);
  const handleUpsellSel = useCallback((next: Record<string, number>) => {
    setUpsellSel(next);
    writePendingAddons(next);
  }, []);
  const handlePackageSel = useCallback((next: Record<string, number>) => {
    setPackageSel(next);
    writePendingPackages(next);
  }, []);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState("");
  const [showCal, setShowCal] = useState(false);
  // Agent on-account: capture the guest the agent is booking for.
  // STAGING ONLY: pre-filled with the test guest so a test booking is a few clicks rather than a
  // form (Jeremy, 2026-09-18). stagingGuestPrefill returns null on production — it keys on the
  // staging DATABASE, so a prod build cannot produce it whatever else is misconfigured.
  const testGuest = stagingGuestPrefill();
  const [guestFirst, setGuestFirst] = useState(testGuest?.firstName ?? "");
  const [guestLast, setGuestLast] = useState(testGuest?.lastName ?? "");
  const [guestEmail, setGuestEmail] = useState(testGuest?.email ?? "");
  const [guestDialCountry, setGuestDialCountry] = useState(NZ_COUNTRY);
  const [guestPhone, setGuestPhone] = useState("");
  // The agent's own booking reference (optional) — printed on their monthly invoice line.
  const [agentRef, setAgentRef] = useState("");

  // Remarkables (whole-pool exclusive hire of The Commons) is the config-gated
  // "Exclusive" experience, so "is this a Remarkables booking?" is just the mode.
  // Only where a Commons exists (Bathe) — elsewhere the option isn't offered. Shown in the agent on-account flow too:
  // create_agent_booking threads p_booking_kind (migration 0263), so an agent hire
  // prices as a whole-pool block (no 2% fee) and confirms with booking_kind='remarkables'.
  const rem = config.remarkables;
  const isRem = mode === "exclusive" && !!rem;
  // The private grid = the live (non-empty) categories only.
  const poolTiles = config.privatePools ?? [];
  // The experiences on the toggle — Exclusive only where the venue has a Remarkables.
  const modes: Mode[] = rem ? ["communal", "private", "exclusive"] : ["communal", "private"];
  // Remarkables takes children too (0220), so only communal drops them.
  const childCount = mode === "communal" ? 0 : children;
  // "Come back for more" is priced off the selection at the STANDARD rate (same pool, same party,
  // standard tier — 0413), so it shows before a time is picked and never rises with a peak slot.
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const baseDuration = selected?.duration_minutes ?? duration;
  useEffect(() => {
    if (agent || isRem || adults + childCount < 1) { setBasePrice(null); return; }
    const ctrl = new AbortController();
    // The session length the booking will be priced at (the picked slot's own, else this page's) —
    // the same length set_booking_packages prices "Come back for more" from at checkout.
    fetch(`/api/packages/base-price?venue=${config.venue}&mode=${engineMode}&pool=${encodeURIComponent(poolSlug)}&adults=${adults}&children=${childCount}&date=${date}&duration=${baseDuration}`, { signal: ctrl.signal, cache: "no-store" })
      .then((r) => r.json())
      .then((j) => setBasePrice(typeof j.price === "number" && j.price > 0 ? j.price : null))
      .catch(() => {});
    return () => ctrl.abort();
  }, [agent, isRem, config.venue, engineMode, poolSlug, adults, childCount, date, baseDuration]);

  const party = adults + childCount;
  // Agent on-account requires the full guest contact — first name, a valid email,
  // AND a phone (both contact methods are mandatory, not optional).
  const agentGuestReady =
    !agent ||
    (guestFirst.trim() !== "" && emailRe.test(guestEmail.trim()) && guestPhone.trim() !== "");
  // 7-day chip row; recenters around a date chosen from the full calendar.
  const [weekStart, setWeekStart] = useState(clampOpen(initialDate ?? todayNZ()));
  const dates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  function recenter(d: string) {
    const start = addDays(d, -3);
    setWeekStart(start < openFloor ? openFloor : start);
  }
  // The chips only show weekday + day number — show the month(s) the visible
  // 7-day window covers so the date isn't ambiguous ("June 2026", or
  // "June – July 2026" when the window spans a month boundary).
  const monthLabel = useMemo(() => {
    const first = dates[0], last = dates[dates.length - 1];
    const firstMonth = fmtNZDate(first, { month: "long" });
    const lastMonthYear = fmtNZDate(last, { month: "long", year: "numeric" });
    return firstMonth === fmtNZDate(last, { month: "long" })
      ? lastMonthYear
      : `${firstMonth} – ${lastMonthYear}`;
  }, [dates]);

  // Guest caps come straight from the live Pool Settings values (via
  // lib/party-limits.ts) — no hard-coded numbers, scoped to the SELECTED pool
  // category so a category whose biggest pool is smaller caps correctly.
  // Communal is gated per-slot by the `full` check below; the picker cap is the
  // live communal entry cap.
  // Remarkables: a single Guests counter, party-bounded 2–8 (all 16+); no
  // children, no tier legend. Standard modes derive caps from live Pool Settings.
  const remMin = rem?.minParty ?? 2;
  const remMax = rem?.maxParty ?? 8;
  // Customers are quoted the SOAK (75 min); the pool is RESERVED for a 90-min block
  // (the extra 15 is the clean slot). The engine owns that split: the availability
  // RPC hands back the 75-min soak and the engine reserves the clean tail on top, so
  // the hold, the schedule block and the grid all stay correct without the app ever
  // handling 90. These labels just render the soak window the guest is sold.
  const remSoak = rem?.soakMinutes ?? 75;
  const soakEnd = (startAt: string) => nzTime(new Date(Date.parse(startAt) + remSoak * 60_000).toISOString());
  const childrenMax = isRem ? Math.max(0, remMax - adults) : childrenMaxFor(config, engineMode, adults, poolSlug);
  const adultsMax = isRem ? Math.max(2, remMax - children) : adultsMaxFor(config, engineMode, children, undefined, poolSlug);
  // Minimum group size — live per-pool floor (resources.min_party). Remarkables
  // keeps its own 2-guest floor.
  const adultsMin = isRem ? remMin : adultsMinFor(config, engineMode, poolSlug);

  // Availability is party-aware: it re-fetches on venue/date/duration AND on a
  // change of adults/children, because a category time is only offered when a free
  // pool in it can actually fit the group — /api/availability drops the pools that
  // can't seat the chosen party (per-pool pax scoping: Canopy Pool 1 fits 4, Pool 2
  // only 2), so the server drops a time whose only free pool is too small (the
  // "cart clears when booking with children" bug). `party=1` preserves
  // get_availability's pricing / communal behaviour; adults+children drive the
  // per-pool exclusive filter. The chosen time is kept across a re-fetch when it
  // still fits (via a functional setSelected so the callback identity is stable),
  // else cleared (its pool no longer seats the party).
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // The customer's OWN live hold must not make their own slot look taken when they
      // come back to the picker (browser back from /checkout). Read it imperatively on
      // every call — it's set outside React state (sessionStorage), so it must not join
      // the dependency array or a back-navigation wouldn't re-read it. Both RPC branches
      // honour p_exclude_booking_id.
      const pending = readPendingHold(config.venue);
      const exclude = pending ? `&excludeBookingId=${pending.bookingId}` : "";
      // Remarkables uses its own engine RPC (90-min whole-pool blocks, priced for
      // the party at the TIER each block starts in). No per-pool grouping — the
      // whole Commons is the product.
      const url = isRem
        ? `/api/availability?venue=${config.venue}&date=${date}&kind=remarkables&party=${party}&adults=${adults}&children=${childCount}${exclude}`
        // `agent=1` opts OUT of time-of-day child pricing (kids_free_windows, 0348).
        // An agent books through create_agent_booking, which is deliberately unthreaded
        // and invoices full child rates — so showing the agent a discounted card would
        // quote one number and bill another.
        : `/api/availability?venue=${config.venue}&date=${date}&duration=${duration}&party=1&adults=${adults}&children=${childCount}${exclude}${agent ? "&agent=1" : ""}`;
      const res = await fetch(url);
      const json = await res.json();
      const next: Slot[] = json.slots ?? [];
      setSlots(next);
      setSelected((prev) =>
        prev ? (next.find((s) => s.resource_slug === prev.resource_slug && s.start_at === prev.start_at) ?? null) : null,
      );
    } catch {
      setError("Couldn’t load availability. Please try again.");
      setSelected(null);
    } finally {
      setLoading(false);
    }
    // isRem (not poolSlug) is the dependency: switching BETWEEN private categories
    // reuses the one grouped payload, but crossing into/out of Remarkables swaps
    // the RPC, so only that boundary re-fetches.
  }, [config.venue, date, duration, party, adults, childCount, isRem]);

  useEffect(() => { load(); }, [load]);

  // Funnel: view_item once per booking-page view (GTM → GA4 view_item + Meta ViewContent).
  // GA4 ecommerce convention — the container reads ecommerce.* (DLV - ecommerce.items …).
  useEffect(() => {
    trackClientEvent("view_item", {
      ecommerce: { currency: "NZD", items: [{ item_id: config.venue, item_name: config.brand, item_category: mode }] },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.venue]);

  const slotsBySlug = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of slots) {
      if (!m.has(s.resource_slug)) m.set(s.resource_slug, []);
      m.get(s.resource_slug)!.push(s);
    }
    return m;
  }, [slots]);

  // Time grid: communal → the communal space; private w/ sub-types → chosen pool;
  // private w/o sub-types (Thermae's identical saunas) → aggregate by start time.
  const timeSlots = useMemo(() => {
    let arr: Slot[];
    // Remarkables: one block list for the whole Commons — no pool grouping.
    if (isRem) arr = slots;
    else if (mode === "communal") arr = slotsBySlug.get(config.communalSpace.slug) ?? [];
    else if (config.privatePools) arr = slotsBySlug.get(poolSlug) ?? [];
    else {
      const byStart = new Map<string, Slot>();
      for (const s of slots) if (s.booking_model === "exclusive" && !byStart.has(s.start_at)) byStart.set(s.start_at, s);
      arr = [...byStart.values()];
    }
    return arr.slice().sort((a, b) => a.start_at.localeCompare(b.start_at));
  }, [slots, slotsBySlug, mode, isRem, poolSlug, config]);
  // Sold-out communal times are HIDDEN for everyone (Jeremy 2026-09-08 — was a
  // greyed "Full" tile on the customer site, hidden only for agents). `full` is
  // only ever true for communal (a taken private/Remarkables pool simply isn't
  // returned), so this only touches the communal picker; the render still
  // carries the "Full" fallback in case a slot sells out between fetch and paint.
  const visibleSlots = useMemo(
    () => timeSlots.filter((s) => !(mode === "communal" && s.seats_left != null && s.seats_left < party)),
    [timeSlots, mode, party],
  );
  const tiersPresent = useMemo(() => TIER_ORDER.filter((t) => visibleSlots.some((s) => s.tier_name === t)), [visibleSlots]);

  // Clear an out-of-capacity communal selection when party grows.
  useEffect(() => {
    if (selected && mode === "communal" && selected.seats_left != null && selected.seats_left < party) setSelected(null);
  }, [party, selected, mode]);

  // Price: recompute on selection / guest change WITHOUT touching availability.
  useEffect(() => {
    if (!selected) { setSubtotal(0); setAdultSubtotal(0); setChildSubtotal(0); setKidsFree(false); return; }
    // Remarkables: get_remarkables_availability already returns each block priced
    // for THIS party at THIS block's tier, and load() re-fetches on a guest change
    // — so read the slot price straight off (no /api/price round-trip needed).
    if (isRem) {
      const sub = Number(selected.price ?? 0);
      setSubtotal(sub); setAdultSubtotal(sub); setChildSubtotal(0); setKidsFree(false);
      return;
    }
    const ctrl = new AbortController();
    // `start` carries the chosen slot's instant so time-of-day pricing (kids_free_windows,
    // 0348) resolves here too — without it the summary would re-quote children at full
    // price right after the time card showed them free. Omitted in the AGENT flow, which
    // books via create_agent_booking at full child rates (see the availability fetch).
    fetch(`/api/price?resource=${selected.resource_id}&tier=${selected.tier_id}&duration=${selected.duration_minutes ?? duration}&adults=${adults}&children=${childCount}&date=${date}${agent ? "" : `&start=${encodeURIComponent(selected.start_at)}`}`, { signal: ctrl.signal })
      .then((r) => r.json()).then((j) => {
        const sub = Number(j.subtotal ?? 0);
        setSubtotal(sub);
        setAdultSubtotal(Number(j.adultSubtotal ?? sub));
        setChildSubtotal(Number(j.childSubtotal ?? 0));
        setKidsFree(j.kidsFree === true);
      }).catch(() => {});
    return () => ctrl.abort();
  }, [selected, adults, childCount, duration, date, isRem]);

  // Keep counts within caps (e.g. when prefilled from the home pill, or a cap drops).
  useEffect(() => { if (adults > adultsMax) setAdults(adultsMax); }, [adultsMax, adults]);
  useEffect(() => { if (children > childrenMax) setChildren(childrenMax); }, [childrenMax, children]);

  // Stable callback so the tile's report effect doesn't re-fire on every render. Both the
  // The packages row's Upsells tile reports here (one instance; historically two responsive
  // breakpoint; both read the same sessionStorage stash, so they agree).
  const handleAddonLines = useCallback((lines: AddonLine[]) => setAddonLines(lines), []);
  const handlePackageLines = useCallback((lines: PackageLine[]) => setPackageLines(lines), []);
  // Packages only apply to a standard session with a time chosen (never the Remarkables hire,
  // never an agent booking) — mirrors set_booking_packages, so the page total never includes a
  // line checkout would drop.
  // Visible from the start (not gated on a time) so a guest scrolling down sees them; never on the
  // Remarkables hire or an agent booking — mirrors set_booking_packages / set_booking_addons.
  const packagesOn = !agent && !isRem;
  const addonsTotal = useMemo(
    () => Math.round(
      (addonLines.reduce((s, l) => s + l.total, 0) +
        (packagesOn ? packageLines.reduce((s, l) => s + l.total, 0) : 0)) * 100,
    ) / 100,
    [addonLines, packageLines, packagesOn],
  );
  // Fee/total fold in add-ons the same way the server does (set_booking_addons): the 2%
  // card fee applies to session + add-ons. Agent on-account has no fee and no add-ons.
  // A voucher from a package email's Book link pays first; the 2% card fee is only on what the card
  // still pays — the same rule checkout applies (priceWithVoucherCredit).
  // A link promo discounts the SESSION only (never add-ons/packages) — the revalidatePromo rule.
  const promoDiscount = !agent && linkPromo && selected
    ? (linkPromo.kind === "percent"
        ? Math.round((subtotal * linkPromo.value) / 100 * 100) / 100
        : Math.min(linkPromo.value, subtotal))
    : 0;
  const voucherCredit = !agent && linkVoucher && selected
    // Not against package lines: a gift voucher can't buy a package voucher (card only).
    ? Math.round(Math.min(linkVoucher.balance, subtotal - promoDiscount + addonsTotal - (packagesOn ? packageLines.reduce((t, l) => t + l.total, 0) : 0)) * 100) / 100
    : 0;
  const fee = agent ? 0 : Math.round((subtotal - promoDiscount + addonsTotal - voucherCredit) * 0.02 * 100) / 100;
  // Total = what the visit costs; a linked voucher then shows UNDER it, with what's left to pay.
  const total = Math.round((subtotal - promoDiscount + addonsTotal + fee) * 100) / 100;
  const toPay = Math.round((total - voucherCredit) * 100) / 100;
  // Summary/heading keep the product's FULL name ("The Remarkables") — only the
  // grid tile uses the short "Remarkables".
  const summaryName = isRem ? rem?.title : mode === "communal" ? config.communalSpace.name : config.privatePools ? config.privatePools.find((p) => p.slug === poolSlug)?.name : config.privateSpace?.name;

  async function reserve() {
    if (!selected) return;
    setBooking(true); setError("");
    try {
      // Capture/refresh ad attribution at the moment of intent; persisted on the
      // booking so the server-side conversion can match the click at confirmation.
      const attribution = captureAttribution();
      const res = await fetch("/api/holds", {
        method: "POST", headers: { "Content-Type": "application/json" },
        // Remarkables threads booking_kind + the slot's own duration (the 75-min soak
        // from get_remarkables_availability — create_hold rejects any other value) and
        // never any children. Its tier rides along like any other booking — the block
        // is tier-priced. The engine reserves the 15-min clean tail on top.
        body: JSON.stringify({
          resource_id: selected.resource_id, start_at: selected.start_at,
          // Every slot carries its own length: 75 on off-peak days, 60 on peak (0398).
          duration: selected.duration_minutes ?? duration,
          adults, children: childCount,
          tier_id: selected.tier_id, channel: "web",
          booking_kind: isRem ? "remarkables" : "standard",
          idempotencyKey: makeKey(), attribution,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        if (res.status === 409 && json?.error === "own_hold_in_payment") {
          // The blocker is almost certainly the guest's OWN unfinished payment (another tab).
          // "Almost": identity falls back to ip_hash when the hold has no customer_id yet, so a
          // second guest on the same connection (venue wifi, a household) can land here too.
          // The copy therefore describes the SLOT's state rather than asserting "you already
          // have it" — true either way, and it still says what to do.
          // Do NOT call load() here: the time is very likely still theirs, and refreshing would
          // drop it from the list and make it look gone.
          setError("That time is being held by a payment that hasn’t finished. If that’s you, finish it in your other tab — otherwise it’ll free up in a few minutes.");
        } else if (res.status === 409) {
          // Slot genuinely just taken by someone else — refresh availability
          // (load() drops the now-gone time) and ask them to pick again.
          setError("That time was just taken — please pick another.");
          await load();
        } else if (res.status === 400 && (json?.error === "party_too_large" || json?.error === "party_exceeds_cap")) {
          // The pool behind this time can't fit the group. KEEP their selected
          // time + price (don't wipe the cart) and say exactly why.
          setError(`This pool can’t fit ${partyPhrase(adults, childCount)} at that time — pick another time or reduce your group.`);
        } else if (res.status === 429) {
          setError("Too many holds from your connection just now — wait a moment and try again.");
        } else {
          setError("Couldn’t hold that slot. Please try another time.");
        }
        return;
      }
      // Remember OUR hold before leaving the picker: the query string below dies on the
      // way back (browser back remounts this component with no params), so this is the
      // only record that survives to tell load() which hold is the customer's own.
      writePendingHold({
        bookingId: json.booking_id,
        resourceId: selected.resource_id,
        venue: config.venue,
        startAt: selected.start_at,
        heldUntil: json.held_until,
      });
      // Funnel: begin_checkout (GTM → GA4 begin_checkout + Meta InitiateCheckout).
      trackClientEvent("begin_checkout", {
        ecommerce: {
          currency: "NZD", value: total,
          items: [{ item_id: selected.resource_id, item_name: summaryName, price: subtotal, quantity: party }],
        },
      });
      const params = new URLSearchParams({ booking: json.booking_id, ref: json.booking_ref, held: json.held_until, price: String(subtotal), venue: config.venue });
      router.push(`/checkout?${params}`);
    } catch { setError("Something went wrong. Please try again."); } finally { setBooking(false); }
  }

  // Agent on-account: no payment hold/checkout — create + confirm + accrue in one call.
  async function bookOnAccount() {
    if (!selected || !agentGuestReady) {
      if (selected && !agentGuestReady) setError("Enter the guest's name, email and phone — all are required.");
      return;
    }
    setBooking(true); setError("");
    try {
      // combinePhone strips any country code the guest re-typed (+64 / 0064 / 64)
      // and the trunk zero, then prefixes the selected dial code.
      const e164 = combinePhone(guestDialCountry.dial, guestPhone).replace(/\s+/g, "");
      const res = await fetch("/api/agent/bookings", {
        method: "POST", headers: { "Content-Type": "application/json" },
        // Remarkables mirrors the customer reserve() body: send the slot's own SOAK
        // duration (75 min — create_hold rejects any other value) and booking_kind so
        // the hire prices as a whole-pool block; standard bookings keep the state
        // duration and "standard" kind.
        body: JSON.stringify({
          resource_id: selected.resource_id, start_at: selected.start_at,
          // Every slot carries its own length: 75 on off-peak days, 60 on peak (0398).
          duration: selected.duration_minutes ?? duration,
          adults, children: mode === "communal" ? 0 : children, tier_id: selected.tier_id,
          booking_kind: isRem ? "remarkables" : "standard",
          guest_first: guestFirst.trim(), guest_last: guestLast.trim(),
          guest_phone: e164,
          guest_email: guestEmail.trim(),
          agent_reference: agentRef.trim() || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error ?? "Couldn’t book that slot."); await load(); return; }
      // reference_saved:false = booked fine, but the agent's reference didn't stick — say so there.
      router.push(`/agent/bookings?booked=${encodeURIComponent(json.booking?.booking_ref ?? "")}${json.reference_saved === false ? "&ref_unsaved=1" : ""}`);
    } catch { setError("Something went wrong. Please try again."); } finally { setBooking(false); }
  }

  const eyebrow = "text-[11px] uppercase tracking-[2.42px] text-ink-soft";

  return (
    <>
      <main className="mx-auto w-full max-w-[1280px] flex-1 px-6 pb-28 pt-14 md:px-12 lg:px-24">
        {/* Heading */}
        <div className="mb-11 flex flex-col gap-3.5">
          <p className="text-[12px] uppercase tracking-[2.64px] text-ink-soft">{config.brand} · {config.region}</p>
          <h1 style={{ fontFamily: "var(--font-display)" }} className="text-[clamp(2.75rem,6vw,58px)] font-light leading-none tracking-[-0.58px] text-ink">
            {isRem ? rem?.title : mode === "communal" ? config.communalTitle : config.privateTitle}
          </h1>
          <p className="max-w-[640px] text-[17px] font-light leading-[1.55] text-ink-soft">{isRem ? rem?.sub : mode === "communal" ? config.communalSub : config.privateSub}</p>
        </div>

        <div className="flex flex-col gap-16 lg:flex-row lg:gap-16">
          <div className="flex min-w-0 flex-1 flex-col gap-11">
            {/* Experience — venues with pool sub-types (Bathe) keep the compact
                pill toggle + separate card sections below; a single-space venue
                (Thermae) combines Experience + The space into ONE two-card
                selector, styled like the pool-category cards (Jeremy 2026-09-07). */}
            {!config.privatePools ? (
              <section className="flex flex-col gap-4">
                <p className={eyebrow}>Experience</p>
                <div className="grid gap-4 md:grid-cols-2">
                  {(["communal", "private"] as Mode[]).map((m) => {
                    const on = mode === m;
                    const sp = m === "communal" ? config.communalSpace : config.privateSpace!;
                    const price = m === "communal" ? config.communalPrice : config.privatePrice;
                    const per = m === "communal" ? config.perPerson : null;
                    return (
                      <button key={m} onClick={() => { setMode(m); setSelected(null); if (m === "private" && config.privatePools) centreOn(poolRef); else centreOn(dateRef, guestsRef); }}
                        className={`flex flex-col overflow-hidden rounded-xl border bg-white text-left transition-colors ${on ? "border-[1.5px] border-ink" : "border-ink/[0.14] hover:border-ink/40"}`}>
                        <div className="relative h-[150px] w-full"><Image src={sp.img} alt={sp.name} fill className="object-cover" sizes="(max-width:768px) 100vw, 340px" /></div>
                        <div className="flex flex-col gap-[5px] px-4 pb-[18px] pt-4">
                          <p className="text-[15px] font-medium text-ink">{sp.name}</p>
                          <p className="text-[12.5px] font-light leading-[1.4] text-ink-soft">{sp.blurb}</p>
                          {price && (
                            <p className="text-[11px] uppercase tracking-[0.22px] text-ink-soft">
                              {per ? `from ${per} per person` : price}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : (
            <section ref={experienceRef} className="flex flex-col gap-4">
              <p className={eyebrow}>Experience</p>
              <div className="flex gap-1 rounded-pill bg-cream-warm p-1">
                {modes.map((m) => {
                  const on = mode === m;
                  const label = m === "communal" ? "Communal" : m === "private" ? "Private" : "Exclusive";
                  const desc = m === "communal" ? config.communalDesc : m === "private" ? config.privateDesc : rem?.desc;
                  // Exclusive's "from" is the live Remarkables base-tier price (remarkables_from_price).
                  const remFrom = rem ? config.fromPrices?.[rem.pool.slug] : undefined;
                  const price = m === "communal" ? config.communalPrice : m === "private" ? config.privatePrice : remFrom ? `from $${Number.isInteger(remFrom) ? remFrom : remFrom.toFixed(2)}` : null;
                  return (
                    <button key={m} onClick={() => {
                      setMode(m); setSelected(null);
                      // Raise the party to the hire's 2-guest floor; a private pool may carry its own.
                      const nextMin = m === "exclusive" ? remMin : m === "private" ? adultsMinFor(config, "private", poolSlug) : 1;
                      if (adults < nextMin) setAdults(nextMin);
                      alignTop(experienceRef);
                    }} className={`flex flex-1 flex-col items-center gap-0.5 rounded-pill py-3 transition-colors ${on ? "bg-ink" : ""}`}>
                      <span className={`text-[13px] uppercase tracking-[0.52px] ${on ? "text-ink-on-dark" : "text-ink"}`}>{label}</span>
                      {/* One subline for every tab: the descriptor always shows; the price
                          hint is appended inline on DESKTOP only (md:inline, not md:block —
                          block would push it onto a second row instead of "Desc · from $XX"). */}
                      {desc && (
                        <span className={`text-[11px] font-light ${on ? "text-ink-on-dark/70" : "text-ink-soft"}`}>
                          {desc}
                          {price && <span className="hidden md:inline"> · {price}</span>}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
            )}

            {/* Pool cards (private w/ sub-types) OR The Space card — single-space
                venues carry this info in the combined selector above. */}
            {!config.privatePools ? null : mode === "private" && config.privatePools ? (
              <section ref={poolRef} className="flex flex-col gap-4">
                <p className={eyebrow}>Your pool</p>
                {/* A horizontal carousel at EVERY width — on desktop too, so the tiles never
                    squeeze into a grid beside the summary (Jeremy 2026-10-29, interim while the
                    private option list is reviewed). The next tile peeks in to show there's more. */}
                <div className="grid gap-3 md:gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(1, poolTiles.length)}, minmax(0, 1fr))` }}>
                  {poolTiles.map((p) => {
                    const on = poolSlug === p.slug;
                    const has = (slotsBySlug.get(p.slug) ?? []).length > 0;
                    return (
                      <button key={p.slug} onClick={() => {
                        setPoolSlug(p.slug); setSelected(null); centreOn(dateRef, guestsRef);
                        // Raise the party to the tile's minimum group size — a private pool
                        // may carry its own floor. A party below it would ask the engine for
                        // a booking it must reject.
                        const nextMin = adultsMinFor(config, "private", p.slug);
                        if (adults < nextMin) setAdults(nextMin);
                      }} className={`flex min-w-0 flex-col overflow-hidden rounded-xl border bg-white text-left transition-colors ${on ? "border-[1.5px] border-ink" : "border-ink/[0.14] hover:border-ink/40"}`}>
                        <div className="relative h-[150px] w-full"><Image src={p.img} alt={p.name} fill className="object-cover" sizes="(max-width:640px) 100vw, 257px" /></div>
                        <div className="flex flex-col gap-[5px] px-4 pb-[18px] pt-4">
                          <p className="text-[15px] font-medium text-ink">{p.name}</p>
                          <p className="text-[12.5px] font-light leading-[1.4] text-ink-soft">{p.blurb}</p>
                          <p className="text-[11px] uppercase tracking-[0.22px] text-ink-soft">
                            {has
                              ? `${adultsMinFor(config, "private", p.slug)}–${adultsMaxFor(config, "private", 0, undefined, p.slug)} guests`
                              : "No times this day"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : (
              <section className="flex flex-col gap-4">
                <p className={eyebrow}>The space</p>
                {(() => {
                  const sp = isRem && rem ? rem.pool : mode === "communal" ? config.communalSpace : config.privateSpace!;
                  return (
                    <div className="flex items-stretch overflow-hidden rounded-[14px] border border-ink/[0.14] bg-white">
                      {/* Wider than the old 120px strip so the Commons photo carries the
                          same visual weight as the private-pool cards' images — on mobile
                          and desktop (~200px). */}
                      <div className="relative min-h-[132px] w-[160px] shrink-0 md:w-[200px]"><Image src={sp.img} alt={sp.name} fill className="object-cover" sizes="(max-width:768px) 160px, 200px" /></div>
                      <div className="flex flex-col justify-center gap-1 px-4 py-4">
                        <p className="text-[16px] font-medium text-ink">{sp.name}</p>
                        <p className="text-[13px] font-light leading-[1.5] text-ink-soft">{sp.blurb}</p>
                      </div>
                    </div>
                  );
                })()}
              </section>
            )}

            {/* Date */}
            <section ref={dateRef} className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2.5">
                  <p className={eyebrow}>Date</p>
                  <span className="text-[12px] font-light text-ink-soft">{monthLabel}</span>
                </div>
                <button onClick={() => setShowCal(true)} className="text-[12px] font-light text-mocha hover:text-ink">View full calendar</button>
              </div>
              {config.openingDate && openFloor === config.openingDate && (
                <p className="text-[12.5px] font-light text-mocha">
                  {config.brand} opens {fmtDateLong(config.openingDate)} — sessions are available from opening day.
                </p>
              )}
              <div className="-mx-6 flex gap-2.5 overflow-x-auto px-6 pb-2 [&::-webkit-scrollbar]:hidden md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
                {dates.map((d) => {
                  const on = date === d;
                  return (
                    <button key={d} onClick={() => { setDate(d); centreOn(timeRef); }} className={`shrink-0 flex flex-col items-center gap-1 rounded-xl px-[18px] py-3 transition-colors ${on ? "bg-ink" : "border border-ink/[0.14] bg-white hover:border-ink/40"}`}>
                      <span className={`text-[11px] uppercase tracking-[1.32px] ${on ? "text-ink-on-dark/70" : "text-ink-soft"}`}>{fmtWeekday(d)}</span>
                      <span style={{ fontFamily: "var(--font-display)" }} className={`text-[24px] leading-none ${on ? "text-ink-on-dark" : "text-ink"}`}>{fmtDay(d)}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Guests — placed ABOVE Time on purpose: guest count drives an
                availability re-fetch, so picking guests first keeps the time grid
                from re-shuffling under the user after they've chosen a slot. */}
            <section ref={guestsRef} className="flex flex-col gap-4">
              <p className={eyebrow}>{isRem ? "How many guests" : mode === "communal" ? "How many people" : "Who’s coming"}</p>
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-10">
                <GuestCounter label={mode === "communal" ? "Guests" : "Adults"} sub={isRem ? "Aged 16+" : config.venue === "thermae" ? "Aged 16+" : mode === "communal" ? "Adults 16+" : "Aged 18+"} value={adults} min={adultsMin} max={adultsMax} onChange={setAdults} />
                {mode !== "communal" && config.allowChildren && <GuestCounter label="Children" sub="Aged 5–17" value={children} min={0} max={childrenMax} onChange={setChildren} />}
              </div>
              {/* Remarkables shows the counter only — the min/max is already enforced
                  by the counter's own +/− limits and "Aged 16+" sits under its label,
                  so the whole helper line (and its wrapper) drops out rather than
                  leaving an empty gap. */}
              {/* A group larger than one pool is built by staff, not sold online, so every helper
                  line ends with the way to ask for one. Only "Contact Us" is the link — "Large
                  Group?" is the prompt, not the action. The Remarkables ceiling is read from the
                  live party bound (adultsMax), which comes from the hire's settings header, so
                  raising the cap in Pool Settings moves this copy with it. */}
              <p className="text-[13px] font-light text-ink-soft">
                {isRem
                  ? `Up to ${adultsMax} guests allowed.`
                  : mode === "communal"
                    ? config.venue === "thermae"
                      ? "A shared sauna, steam room and cold plunge session. Adults only — 16+."
                      : "A shared soak with up to 8 other guests."
                    : config.venue === "thermae"
                      ? "Adults only — Thermae is a 16+ experience"
                      : "Up to 4 adults, or 2 adults + 3 children. Third child soaks free."}{" "}
                Large Group?{" "}
                <a
                  href="https://www.aluume.com/contact-us"
                  className="underline underline-offset-2 hover:text-ink"
                >
                  Contact Us
                </a>
              </p>
            </section>

            {/* Time */}
            <section ref={timeRef} className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className={eyebrow}>Choose a time</p>
                <div className="flex items-center gap-3">
                  {mode === "communal" && <span className="text-[12px] font-light text-ink-soft">{config.perPerson} per person</span>}
                  {/* No session-length picker (Jeremy, 2026-09-30): the guest picks a date and a time;
                      each venue's session length is what it is. */}
                </div>
              </div>
              {tiersPresent.length > 0 && (
                <div className="flex flex-wrap gap-x-[18px] gap-y-2">
                  {tiersPresent.map((t) => (<span key={t} className="flex items-center gap-[7px]"><span className="size-2 rounded-full" style={{ background: TIER_COLOR[t] }} /><span className="text-[12px] font-light text-ink-soft">{t}</span></span>))}
                </div>
              )}
              {loading ? (
                <p className="text-[14px] font-light text-ink-soft">Loading availability…</p>
              ) : visibleSlots.length === 0 ? (
                <p className="text-[14px] font-light text-ink-soft">No times here on this day — try another date.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:justify-center">
                  {visibleSlots.map((s) => {
                    const on = selected?.start_at === s.start_at && selected?.resource_id === s.resource_id;
                    const full = mode === "communal" && s.seats_left != null && s.seats_left < party;
                    return (
                      <button key={`${s.resource_id}-${s.start_at}`} data-testid="time-slot" disabled={full} onClick={() => { setSelected(s); if (!agent && !isRem) centreOn(packagesRef); }}
                        className={`flex w-full flex-col gap-2 rounded-xl px-4 py-[13px] text-left transition-colors sm:w-[150px] ${on ? "bg-ink" : full ? "border border-ink/[0.08] bg-cream-warm" : "border border-ink/[0.14] bg-white hover:border-ink/40"}`}>
                        <span className="flex items-center gap-[7px]">
                          {!full && <span className="size-2 rounded-full" style={{ background: TIER_COLOR[s.tier_name] ?? "#A69580" }} />}
                          <span className={`text-[15px] font-medium leading-none ${on ? "text-ink-on-dark" : full ? "text-ink-soft" : "text-ink"}`}>{nzTime(s.start_at)}</span>
                        </span>
                        {/* Every experience now shows tier + price. Communal's price is
                            PER PERSON (availability is fetched at party=1); private and
                            Remarkables are the whole-booking price for the chosen party. */}
                        <span className={`text-[12px] font-light ${on ? "text-ink-on-dark/70" : "text-ink-soft"}`}>
                          {full ? "Full" : `${s.tier_name} · ${nzd(s.price)}`}
                        </span>
                        {/* Second line: communal keeps its scarcity cue; Remarkables shows
                            the 75-min SOAK window's end (never the 90-min block's). */}
                        {!full && mode === "communal" && s.seats_left != null && (
                          <span className={`text-[11px] font-light ${on ? "text-ink-on-dark/60" : "text-ink-soft"}`}>{s.seats_left} left</span>
                        )}
                        {isRem && (
                          <span className={`text-[11px] font-light ${on ? "text-ink-on-dark/60" : "text-ink-soft"}`}>Ends {soakEnd(s.start_at)}</span>
                        )}
                        {/* Session length, pinned to the bottom of the tile so it lines up across a
                            row even when a price wraps — 60 on peak days, 75 off-peak (0398). */}
                        {!full && (
                          <span data-testid="time-slot-minutes" className={`mt-auto text-[11px] font-light ${on ? "text-ink-on-dark/45" : "text-ink-mute"}`}>
                            {s.duration_minutes ?? duration} minutes
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Packages (0366 → 0382) — ONE row of three tiles directly under the time slots:
                cross promotion · class packs · upsells. Visible before a time is picked; picking a
                time centres it. Upsells replaced the old "Forgot your togs?" add-ons tile. */}
            {packagesOn && (
              <PackagesInfoTile
                venue={config.venue}
                partySize={party}
                sessionPrice={basePrice}
                sel={packageSel}
                onSel={handlePackageSel}
                upSel={upsellSel}
                onUpSel={handleUpsellSel}
                onPackageLines={handlePackageLines}
                onUpsellLines={handleAddonLines}
                sectionRef={packagesRef}
              />
            )}

          </div>

          {/* Summary rail — desktop */}
          <aside className="hidden shrink-0 lg:flex lg:w-[380px] lg:flex-col lg:gap-5">
            <div className="flex flex-col gap-[22px] rounded-2xl border border-ink/10 bg-white p-8 shadow-[0px_20px_44px_-26px_rgba(57,51,48,0.16)] lg:sticky lg:top-6">
              <p className={eyebrow}>{config.venue === "thermae" ? "Your float" : "Your soak"}</p>
              <div className="flex flex-col gap-[3px]">
                <p style={{ fontFamily: "var(--font-display)" }} className="text-[30px] leading-[1.05] tracking-[-0.3px] text-ink">{summaryName}</p>
                <p className="text-[13px] font-light text-ink-soft">{isRem ? `Exclusive hire · ${config.brand}` : mode === "communal" ? `Communal · ${config.brand}` : `Private · ${config.brand}`}</p>
              </div>
              <div className="h-px w-full bg-ink/[0.12]" />
              <dl className="flex flex-col gap-3">
                <SummaryRow k="Date" v={fmtDateLong(date)} />
                <SummaryRow k="Time" v={selected ? (isRem ? `${nzTime(selected.start_at)} – ${soakEnd(selected.start_at)}` : `${nzTime(selected.start_at)} · ${selected.tier_name}`) : "Select a time"} />
              </dl>
              <div className="h-px w-full bg-ink/[0.12]" />
              <dl className="flex flex-col gap-3">
                {/* Guest counts are conveyed by the priced rows below — adults and
                    children each get their own "N × …" line (no separate descriptor).
                    Remarkables is a single whole-pool price, not per-head. */}
                {isRem ? (
                  <SummaryRow k={`Exclusive hire · ${adults} guests`} v={selected ? nzd(subtotal) : "—"} />
                ) : (
                <SummaryRow k={mode === "communal" ? `${adults} × Guest` : `${adults} × Adult`} v={selected ? nzd(adultSubtotal) : "—"} />
                )}
                {childCount > 0 && (
                  <SummaryRow k={`${childCount} × Child`} v={selected ? nzd(childSubtotal) : "—"} />
                )}
                {/* Say WHY the child line is $0.00 — on its own it reads like an error.
                    kidsFree comes from the server, not from "the number is zero", so a
                    venue with no child bands can never advertise a deal it isn't running. */}
                {childCount > 0 && selected && kidsFree && (
                  <p
                    className="-mt-[6px] mb-[2px]"
                    style={{ fontFamily: "var(--font-sans)", fontWeight: 300, fontSize: 11.5, lineHeight: 1.4, color: "#62684b" }}
                  >
                    {KIDS_FREE_LABEL}
                  </p>
                )}
                {/* Add-ons chosen above show by name here (and fold into the fee/total),
                    so the guest sees the running cost before they reach checkout. */}
                {addonLines.map((ln, i) => (
                  <SummaryRow key={i} k={ln.qty > 1 ? `${ln.qty} × ${ln.name}` : ln.name} v={nzd(ln.total)} />
                ))}
                {packagesOn && packageLines.map((ln, i) => (
                  <SummaryRow key={`p${i}`} k={ln.qty > 1 ? `${ln.qty} × ${ln.name}` : ln.name} v={nzd(ln.total)} />
                ))}
                {linkPromo && !agent && (
                  <SummaryRow k={`Code ${linkPromo.code} · ${linkPromo.kind === "percent" ? `${linkPromo.value}% off` : `${nzd(linkPromo.value)} off`}`} v={selected ? `− ${nzd(promoDiscount)}` : "applied"} />
                )}
                {!agent && <SummaryRow k="Booking fee (2%)" v={selected ? nzd(fee) : "—"} />}
                <div className="h-px w-full bg-ink/[0.12]" />
                <div className="flex items-center justify-between"><span className="text-[15px] font-medium text-ink">Total</span><span className="text-[16px] font-medium text-ink">{selected ? nzd(total) : "—"}</span></div>
                {/* A package voucher from the email's Book link — already applied (Jeremy, 2026-09-30). */}
                {linkVoucher && !agent && (
                  <>
                    <SummaryRow k={`Voucher ${linkVoucher.code.toUpperCase()} applied`} v={selected ? `− ${nzd(voucherCredit)}` : `${nzd(linkVoucher.balance)} to use`} />
                    {selected && (
                      <div className="flex items-center justify-between"><span className="text-[15px] font-medium text-ink">To pay</span><span className="text-[16px] font-medium text-ink">{nzd(toPay)}</span></div>
                    )}
                  </>
                )}
              </dl>
              {agent && selected && (
                <div className="flex flex-col gap-3">
                  <p className={eyebrow}>Guest details · name, email &amp; phone required</p>
                  <div className="flex gap-3">
                    <input value={guestFirst} onChange={(e) => setGuestFirst(e.target.value)} placeholder="First name" className="w-full border-b border-ink/20 bg-transparent pb-1.5 text-[14px] text-ink outline-none focus:border-ink" />
                    <input value={guestLast} onChange={(e) => setGuestLast(e.target.value)} placeholder="Last name" className="w-full border-b border-ink/20 bg-transparent pb-1.5 text-[14px] text-ink outline-none focus:border-ink" />
                  </div>
                  <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="Email" className="w-full border-b border-ink/20 bg-transparent pb-1.5 text-[14px] text-ink outline-none focus:border-ink" />
                  <div className="flex items-end gap-3">
                    <div className="w-[110px] shrink-0">
                      <CountrySelect value={guestDialCountry.iso} onChange={(c) => setGuestDialCountry(c)} mode="dial" />
                    </div>
                    <input type="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="Phone" className="w-full border-b border-ink/20 bg-transparent pb-1.5 text-[14px] text-ink outline-none focus:border-ink" />
                  </div>
                  <input value={agentRef} onChange={(e) => setAgentRef(e.target.value)} maxLength={AGENT_REFERENCE_MAX} placeholder="Your booking reference (optional)" aria-label="Your booking reference (optional)" className="w-full border-b border-ink/20 bg-transparent pb-1.5 text-[14px] text-ink outline-none focus:border-ink" />
                </div>
              )}
              <button onClick={agent ? bookOnAccount : reserve} disabled={!selected || booking || !agentGuestReady} className="flex h-[54px] w-full items-center justify-center gap-2.5 rounded-pill bg-ink text-[13px] uppercase tracking-[2.08px] text-ink-on-dark transition-colors hover:bg-accent disabled:opacity-40">{booking && <Spinner size={16} className="text-ink-on-dark" />}{agent ? (booking ? "Booking…" : "Book on account") : (booking ? "Holding…" : "Continue to checkout")}</button>
              {error && <p className="text-[12px] text-corten">{error}</p>}
              {linkVoucherError && <p className="text-[12px] text-corten">{linkVoucherError}</p>}
              {linkPromoError && <p className="text-[12px] text-corten">{linkPromoError}</p>}
              <div className="flex flex-col gap-2 text-[12px] font-light">
                {agent ? (
                  <p className="text-ink-soft">No payment now — this booking is added to your monthly statement.</p>
                ) : (
                  <>
                    {/* The towel/arrival-drink inclusion is Bathe's — Thermae's
                        inclusions copy is pending from Jeremy (card a2e2f058). */}
                    {config.venue !== "thermae" && (
                      <p className="text-ink-soft">Towel and a complimentary arrival drink included.</p>
                    )}
                    <p className="text-ink-soft">All prices NZD, incl GST. We hold your space for 5 minutes at checkout.</p>
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile sticky bottom bar */}
      <div className={`fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-4 border-t border-ink/10 bg-cream px-6 pt-3.5 pb-[calc(14px+env(safe-area-inset-bottom))] shadow-[0px_-8px_24px_-12px_rgba(57,51,48,0.18)] transition-transform duration-300 ease-out lg:hidden ${selected ? "translate-y-0" : "translate-y-full"}`}>
        <div className="min-w-0">
          <p style={{ fontFamily: "var(--font-display)" }} className="text-[22px] leading-none text-ink">{selected ? nzd(total) : "—"}</p>
          <p className="truncate text-[11px] font-light text-ink-soft">{summaryName}{selected ? ` · ${nzTime(selected.start_at)}` : " · select a time"}{linkPromo && !agent ? ` · ${linkPromo.code} applied` : ""}</p>
        </div>
        <button onClick={agent ? bookOnAccount : reserve} disabled={!selected || booking || !agentGuestReady} className="flex shrink-0 items-center justify-center gap-2 rounded-pill bg-ink px-7 py-3.5 text-[13px] uppercase tracking-[1.5px] text-ink-on-dark transition-colors hover:bg-accent disabled:opacity-40">{booking && <Spinner size={15} className="text-ink-on-dark" />}{booking ? (agent ? "Booking" : "Holding") : agent ? "Book" : "Continue"}</button>
      </div>

      {showCal && <Calendar value={date} min={openFloor} onSelect={(d) => { setDate(clampOpen(d)); recenter(clampOpen(d)); centreOn(timeRef); }} onClose={() => setShowCal(false)} />}
    </>
  );
}

function GuestCounter({ label, sub, value, min, max, onChange }: { label: string; sub: string; value: number; min: number; max: number; onChange: (n: number) => void }) {
  return (
    <div className="flex w-full items-center justify-between gap-5 sm:w-auto sm:justify-start">
      <div className="flex flex-col gap-0.5"><span className="text-[15px] font-medium text-ink">{label}</span><span className="text-[12px] font-light text-ink-soft">{sub}</span></div>
      <div className="flex items-center rounded-pill border border-ink/[0.18]">
        <button aria-label={`decrease ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)} className="flex size-[42px] items-center justify-center text-[20px] font-light text-ink-soft disabled:opacity-30">–</button>
        <span className="flex h-[42px] w-[46px] items-center justify-center text-[16px] text-ink">{value}</span>
        <button aria-label={`increase ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)} className="flex size-[42px] items-center justify-center text-[20px] font-light text-ink disabled:opacity-30">+</button>
      </div>
    </div>
  );
}

function SummaryRow({ k, v }: { k: string; v: string }) {
  return (<div className="flex items-center justify-between"><dt className="text-[13px] font-light text-ink-soft">{k}</dt><dd className="text-right text-[14px] text-ink">{v}</dd></div>);
}
